<template>
    <div class="container">
        <vrm-form v-if="!completed" @submit="submit">
            <div class="row">
                <div class="col-xs-12 col-md-10 col-md-offset-1">
                    <h1 class="vrm-page-title">
                        <template v-if="isResetMode">
                            {{
                                $t('mfa.mobile_wizard_title_reset_two_factor_authentication', {
                                    i18nFallback: 'Resetting two-factor authentication',
                                })
                            }}
                        </template>
                        <template v-else>
                            {{
                                $t('mfa.mobile_wizard_title_setting_two_factor_authentication', {
                                    i18nFallback: 'Setting up two-factor authentication',
                                })
                            }}
                        </template>
                    </h1>
                    <p>
                        {{
                            $t('mfa.mobile_wizard_instructions_setting_two_factor_authentication', {
                                i18nFallback:
                                    'You will need an authenticator app to complete this process.',
                            })
                        }}
                    </p>
                </div>
            </div>
            <div class="row">
                <div class="col-xs-12 col-md-6 col-md-offset-1">
                    <div class="vrm-mobile-text">
                        <h4>
                            <strong>
                                1.
                                {{
                                    $t(
                                        'mfa.mobile_wizard_first_step_title_two_factor_authentication',
                                        {
                                            i18nFallback:
                                                'Scan the QR code with your authenticator app',
                                        },
                                    )
                                }}
                            </strong>
                        </h4>
                        <p v-if="isResetMode">
                            {{
                                $t('mfa.mobile_wizard_explainer_end_reset', {
                                    i18nFallback:
                                        'In order to safely unlink all your authenticator devices, you have to first link a new device. Once you have verified the new code the other devices will be unlinked.',
                                })
                            }}
                        </p>
                        <p v-else>
                            {{
                                $t(
                                    'mfa.mobile_wizard_first_step_instructions_two_factor_authentication',
                                    {
                                        i18nFallback:
                                            'When successfully scanned, the app may ask you to provide a name. Please finish adding the profile to the app before continuing. You must verify at least one code successfully before two-factor verification with an authenticator app is enabled.',
                                    },
                                )
                            }}
                        </p>
                        <div class="vrm-mobile-qr-code">
                            <vrm-mfa-qr-code :href="inviteUrl" />
                        </div>
                        <p>
                            {{
                                $t('mfa.mobile_wizard_cant_scan_qr_code', {
                                    i18nFallback:
                                        'If you can’t scan the code, you can enter the secret key below into your authenticator app.',
                                })
                            }}
                        </p>
                        <div class="vrm-mobile-text-key">
                            <div v-if="showKey" class="vrm-mobile-text-input " type="text" disabled>
                                <a v-if="showKey" :href="inviteUrl" class="vrm-mobile-text-link">
                                    {{ secret }}
                                </a>
                            </div>
                            <input v-else v-model="secret" type="password" disabled />
                            <div class="vrm-mobile-text-icons">
                                <a
                                    v-if="showKey"
                                    v-tooltip="
                                        $t('mfa.mobile_wizard_hide_key', {
                                            i18nFallback: 'Hide Key',
                                        })
                                    "
                                    class="vrm-table__icon--link sg-vrm-icon_123_eye-blocked-16px"
                                    @click="showHideKey"
                                />
                                <a
                                    v-else
                                    v-tooltip="
                                        $t('mfa.mobile_wizard_show_key', {
                                            i18nFallback: 'Show Key',
                                        })
                                    "
                                    class="vrm-table__icon--link sg-vrm-icon_122_eye-16px"
                                    @click="showHideKey"
                                />
                                <a
                                    v-tooltip="
                                        $t('mfa.mobile_wizard_copy_key', {
                                            i18nFallback: 'Copy Key',
                                        })
                                    "
                                    class="vrm-table__icon--link sg-vrm-icon_095_report_16px"
                                    @click="copyToClipboard"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="vrm-mobile-text">
                        <h4>
                            <strong>
                                2.
                                {{
                                    $t(
                                        'mfa.mobile_wizard_second_step_title_two_factor_authentication',
                                        {
                                            i18nFallback: 'Enter the verification code',
                                        },
                                    )
                                }}
                            </strong>
                        </h4>
                        <p class="vrm-mobile-text-paragraph">
                            {{
                                $t(
                                    'mfa.mobile_wizard_second_step_instructions_two_factor_authentication',
                                    {
                                        i18nFallback:
                                            'Enter the code provided by your authenticator app.',
                                    },
                                )
                            }}
                        </p>
                        <template v-if="!inviteLoading">
                            <div class="row">
                                <div class="col-xs-12 col-md-6">
                                    <vrm-text-field
                                        v-model="code"
                                        :disabled="busy"
                                        name="one-time-code"
                                        autocomplete="telephone"
                                        inputmode="numeric"
                                        pattern="[0-9]*"
                                        placeholder="000000"
                                        required
                                    />
                                </div>
                                <div class="col-xs-12 col-md-1">
                                    <div class="">
                                        <label class="sg-textfield-label">&nbsp;</label>
                                        <button
                                            type="submit"
                                            class="sg-btn sg-blue"
                                            :disabled="busy"
                                        >
                                            {{
                                                $t('mfa.mobile_wizard_verify', {
                                                    i18nFallback: 'Verify',
                                                })
                                            }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
                <div class="col-xs-12 col-md-4 ">
                    <div class="vrm-mobile-qr-code__mobile">
                        <vrm-mfa-qr-code :href="inviteUrl" />
                    </div>
                </div>
            </div>
        </vrm-form>
        <vrm-form v-else>
            <div class="row">
                <div class="col-sm-12 col-md-10 col-md-offset-1">
                    <h1 class="vrm-page-title">
                        {{
                            $t('mfa.mobile_wizard_success_title', {
                                i18nFallback: 'Authenticator linked successfully',
                            })
                        }}
                    </h1>
                    <p v-if="isResetMode && resetFirst">
                        {{
                            $t('mfa.mobile_wizard_success_reset', {
                                i18nFallback:
                                    'A new authenticator has been linked, all previously linked devices have been linked. In order to use more devices for authentication, you will have to link them again.',
                            })
                        }}
                    </p>
                    <p>
                        <a @click.prevent="linkAnotherDevice">
                            {{
                                $t('mfa.mobile_wizard_link_another_device', {
                                    i18nFallback: 'Add another device',
                                })
                            }}
                        </a>
                        <br />
                        <router-link to="base.profile.twofactor">
                            {{ $t('mfa.back_to_settings', { i18nFallback: 'Back to settings' }) }}
                        </router-link>
                    </p>
                </div>
            </div>
        </vrm-form>
    </div>
</template>
<style scoped lang="scss"></style>
<script>
import { Vue, Component } from 'vue-property-decorator';
import { AngularRoute, ngInject } from '@/vue/decorators/angular-interop';
import WizardMixin from '@/vue/components/pages/mfa/wizards/WizardMixin';
import VrmForm from '@/vue/components/forms/VrmForm';
import VrmMfaQrCode from '@/vue/components/shared/VrmQrCode';
import VrmTextField from '@/vue/components/forms/VrmTextField';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';

@Component({
    components: { VrmTextField, VrmMfaQrCode, VrmForm },
    mixins: [WizardMixin],
})
@AngularRoute('base.profile.twofactor.mobile', '/mobile-app?mode')
@AngularLocaleBundle('mfa')
@AngularLocaleBundle('diagnostics')
export default class Mobile extends Vue {
    @ngInject()
    vrmNotificationsService;
    @ngInject()
    vrmNotificationType;

    completed = false;
    busy = false;
    inviteLoading = false;
    inviteUrl = null;
    code = '';
    secret = null;
    resetFirst = true;
    showKey = false;

    async copyToClipboard() {
        try {
            await navigator.clipboard.writeText(this.secret);
            this.vrmNotificationsService.add({
                type: this.vrmNotificationType.SUCCESS,
                title: this.$t('diagnostics.clipboard_copy_success', {
                    i18nFallback: 'Content copied to clipboard',
                }),
            });
        } catch (e) {
            this.vrmNotificationsService.add({
                type: this.vrmNotificationType.ERROR,
                title: this.$t('diagnostics.clipboard_copy_success', {
                    i18nFallback: 'Could not write to clipboard',
                }),
                message: e.message,
            });
        }
    }

    get mode() {
        return this.$route.params.mode;
    }

    get isResetMode() {
        return this.mode === 'reset';
    }

    showHideKey() {
        this.showKey = !this.showKey;
    }

    async created() {
        this.inviteLoading = true;
        try {
            const data = await this.fetchSetupData(this.mode);
            if (data) {
                this.inviteUrl = data.url;
                this.secret = data.secret;
            }
        } finally {
            this.inviteLoading = false;
        }
    }

    @ngInject()
    ApiService;

    async fetchSetupData(mode) {
        return await this.$store.dispatch('MfaService/mobileSetup', [mode]);
    }

    linkAnotherDevice() {
        this.completed = false;
        this.busy = false;
        this.code = '';
        this.resetFirst = false;
    }

    async submit() {
        this.busy = true;
        try {
            if (await this.verifyCode()) {
                this.completed = true;
            }
        } finally {
            this.busy = false;
        }
    }

    async verifyCode() {
        return await this.$store.dispatch('MfaService/mobileVerify', [this.secret, this.code]);
    }
}
</script>
